import { useEffect, useState, type KeyboardEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Send } from 'lucide-react'
import { directMessageApi } from '@/services/api/directMessages'

interface DmMessageInputProps {
  conversationId: string
}

export function DmMessageInput({ conversationId }: DmMessageInputProps) {
  const queryClient = useQueryClient()
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setBody('')
    setError(null)
  }, [conversationId])

  const submit = async () => {
    const text = body.trim()
    if (!text || sending) return
    if (text.length > 5000) {
      setError('Maximal 5000 Zeichen')
      return
    }
    setSending(true)
    setError(null)
    try {
      await directMessageApi.sendMessage(conversationId, text)
      setBody('')
      queryClient.invalidateQueries({ queryKey: ['dm-messages', conversationId] })
      queryClient.invalidateQueries({ queryKey: ['direct-messages'] })
      queryClient.invalidateQueries({ queryKey: ['direct-messages-unread-count'] })
    } catch (e) {
      setError((e as { detail?: string }).detail || 'Senden fehlgeschlagen')
    } finally {
      setSending(false)
    }
  }

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void submit()
    }
  }

  return (
    <div className="border-t border-gray-200 bg-white p-3">
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      <div className="flex items-end gap-2">
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          maxLength={5000}
          placeholder="Nachricht schreiben…"
          className="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <button
          type="button"
          onClick={() => void submit()}
          disabled={sending || !body.trim()}
          className="p-2 rounded-lg bg-blue-600 text-white disabled:opacity-40 hover:bg-blue-700"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
